import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, API_BASE_URL } from "./client";

export interface SchedulerStatus {
  scheduler_running: boolean;
  job_in_progress: boolean;
  next_run_iso?: string | null;
  next_daily_run_iso?: string | null;
  next_source_sync_iso?: string | null;
  source_sync_enabled?: boolean;
  source_sync_interval_hours?: number;
  last_run_started_iso?: string | null;
  last_run_completed_iso?: string | null;
  last_run_error?: string | null;
  last_run_report?: Record<string, unknown> | null;
  backup_mode?: string | null;
  accounts_total: number;
  base_path?: string | null;
  accounts_progress?: Record<string, AccountProgress> | null;
  account_labels?: Record<string, string> | null;
}

export interface RunRequest {
  date?: string;
}

export interface AccountProgress {
  status: string;
  progress?: number | null;
  stage?: string | null;
  updated_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  duration_seconds?: number | null;
  total_messages?: number | null;
  total_size_bytes?: number | null;
  unread_messages?: number | null;
  archive_filename?: string | null;
  digest_filename?: string | null;
  error?: string | null;
  detail?: string | null;
  errors?: Array<Record<string, unknown>> | null;
}

export interface AccountSummary {
  name: string;
  years: string[];
  label?: string | null;
}

export interface AccountListResponse {
  total: number;
  accounts: AccountSummary[];
}

export interface DateInfo {
  date: string;
  message_count: number;
  status?: string | null;
  archive_filename?: string | null;
  archive_size_bytes?: number | null;
  archive_sha256?: string | null;
  digest_filename?: string | null;
  total_size_bytes?: number | null;
  error_count?: number | null;
}

export interface DateListResponse {
  account: string;
  year: string;
  total: number;
  limit: number;
  offset: number;
  dates: DateInfo[];
}

export interface AccountStatsSummary {
  name: string;
  label?: string | null;
  archive_days: number;
  messages: number;
  content_size_bytes: number;
  archive_size_bytes: number;
  errors: number;
  first_archive_date?: string | null;
  last_archive_date?: string | null;
  folders: Record<string, number>;
}

export interface YearlyStatsSummary {
  year: string;
  accounts: number;
  archive_days: number;
  messages: number;
  content_size_bytes: number;
  archive_size_bytes: number;
  errors: number;
}

export interface SystemStatsResponse {
  generated_at: string;
  base_path: string;
  accounts_total: number;
  archive_days_total: number;
  total_messages: number;
  content_size_bytes: number;
  archive_size_bytes: number;
  filesystem_bytes: number;
  total_errors: number;
  earliest_archive_date?: string | null;
  latest_archive_date?: string | null;
  accounts: AccountStatsSummary[];
  yearly: YearlyStatsSummary[];
  scheduler_status?: SchedulerStatus | null;
}

export interface EmailInfo {
  uid: string;
  folder: string;
  filename: string;
  filepath: string;
  subject: string;
  sender: string;
  from?: string;
  to: string;
  cc?: string;
  date: string;
  message_id: string;
  size: number;
}

function normalizeEmailInfo(raw: EmailInfo): EmailInfo {
  const sender = typeof raw.sender === "string" && raw.sender.length > 0
    ? raw.sender
    : (typeof raw.from === "string" ? raw.from : "");
  return {
    ...raw,
    sender,
  };
}

function normalizeEmailListResponse(raw: EmailListResponse): EmailListResponse {
  return {
    ...raw,
    emails: (raw.emails ?? []).map((email) => normalizeEmailInfo(email)),
  };
}

function normalizeSearchResponse(raw: SearchResponse): SearchResponse {
  return {
    ...raw,
    results: (raw.results ?? []).map((entry) => ({
      ...entry,
      email: normalizeEmailInfo(entry.email),
    })),
  };
}

export interface EmailListResponse {
  total: number;
  limit?: number;
  offset?: number;
  emails: EmailInfo[];
}

export interface EmailPreview {
  subject: string;
  sender: string;
  to: string;
  cc: string;
  date: string;
  body: string;
  html?: string | null;
  attachments: { id: number; filename: string; content_type?: string; size?: number }[];
}

export interface HealthResponse {
  status: string;
  timestamp: string;
  auth_required: boolean;
  auth_provider?: string | null;
  oidc_issuer?: string | null;
  oidc_client_id?: string | null;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_at: string;
  username: string;
}

export interface APITokenInfo {
  id: string;
  label?: string | null;
  created_at: string;
  expires_at?: string | null;
  last_used_at?: string | null;
  accounts?: string[] | null;
  permissions?: string[] | null;
}

export interface APITokenListResponse {
  total: number;
  tokens: APITokenInfo[];
}

export interface APITokenCreateResponse {
  token: string;
  token_info: APITokenInfo;
}

export interface IAMUserSummary {
  id: string;
  username: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  enabled: boolean;
  roles: string[];
  groups: string[];
}

export interface IAMUserListResponse {
  total: number;
  users: IAMUserSummary[];
}

export interface IAMUserCreateRequest {
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  password: string;
  enabled?: boolean;
  roles?: string[];
}

export interface IAMUserUpdateRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  enabled?: boolean;
  roles?: string[];
}

export interface IAMGroupSummary {
  id: string;
  name: string;
}

export interface IAMGroupListResponse {
  total: number;
  groups: IAMGroupSummary[];
}

export interface RetryPolicyConfig {
  max_retries: number;
  initial_delay: number;
  backoff_multiplier: number;
}

export interface ImapConfig {
  timeout: number;
  batch_size: number;
}

export interface SchedulerConfig {
  run_time: string;
  source_sync_enabled?: boolean;
  source_sync_interval_hours?: number;
}

export interface NotificationSmtpConfig {
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  sender?: string;
  use_tls?: boolean;
}

export interface NotificationConfig {
  enabled: boolean;
  send_on?: string;
  recipients?: string[];
  smtp?: NotificationSmtpConfig;
}

export interface AuthUserConfig {
  username: string;
  password: string;
}

export interface AuthConfig {
  enabled: boolean;
  provider?: string;
  secret_key?: string;
  token_expiry_minutes?: number;
  algorithm?: string;
  issuer_url?: string;
  client_id?: string;
  audience?: string;
  jwks_url?: string;
  jwks_cache_seconds?: number;
  algorithms?: string[];
  users?: AuthUserConfig[];
}

export interface AccountConfig {
  username: string;
  label?: string;
  password: string;
  host: string;
  port?: number;
  folders: string[];
}

export interface S3Config {
  bucket: string;
  region: string;
  prefix?: string;
  endpoint_url?: string;
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
}

export interface ConfigDocument {
  base_path: string;
  backup_mode: string;
  concurrency: number;
  retry_policy: RetryPolicyConfig;
  imap: ImapConfig;
  scheduler: SchedulerConfig;
  notifications: NotificationConfig;
  auth: AuthConfig;
  accounts: AccountConfig[];
  access_control?: {
    allow: string[];
    deny: string[];
  };
  s3?: S3Config | null;
  [key: string]: unknown;
}

export const systemStatusKey = ["system", "status"] as const;

export function useSystemStatus(pollMs = 20_000) {
  return useQuery({
    queryKey: systemStatusKey,
    queryFn: async () => {
      const { data } = await apiClient.get<SchedulerStatus>("/v1/system/status");
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export const systemStatsKey = ["system", "stats"] as const;

export function useSystemStats(pollMs = 120_000) {
  return useQuery({
    queryKey: systemStatsKey,
    queryFn: async () => {
      const { data } = await apiClient.get<SystemStatsResponse>("/v1/system/stats");
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: false
  });
}

export function useApiHealth() {
  return useQuery({
    queryKey: ["system", "health"],
    queryFn: async () => {
      const { data } = await apiClient.get<HealthResponse>("/v1/health");
      return data;
    },
    refetchOnWindowFocus: false,
    refetchInterval: false
  });
}

export function useRunNow() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RunRequest) => {
      const { data } = await apiClient.post("/v1/system/run-now", payload);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
    }
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: async (payload: LoginRequest) => {
      const { data } = await apiClient.post<LoginResponse>("/v1/auth/login", payload);
      return data;
    }
  });
}

export function useApiTokens() {
  return useQuery({
    queryKey: ["api-tokens"],
    queryFn: async () => {
      const { data } = await apiClient.get<APITokenListResponse>("/v1/auth/tokens");
      return data;
    },
    refetchOnWindowFocus: true
  });
}

export function useCreateApiToken() {
  return useMutation({
    mutationFn: async (payload: { label?: string; expires_in_days?: number; accounts?: string[]; permissions?: string[] }) => {
      const { data } = await apiClient.post<APITokenCreateResponse>("/v1/auth/tokens", payload);
      return data;
    }
  });
}

export function useDeleteApiToken() {
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/v1/auth/tokens/${encodeURIComponent(id)}`);
      return true;
    }
  });
}

export function useIamUsers(enabled = true) {
  return useQuery({
    queryKey: ["iam-users"],
    queryFn: async () => {
      const { data } = await apiClient.get<IAMUserListResponse>("/v1/iam/users");
      return data;
    },
    enabled,
    refetchOnWindowFocus: true
  });
}

export function useIamGroups(enabled = true) {
  return useQuery({
    queryKey: ["iam-groups"],
    queryFn: async () => {
      const { data } = await apiClient.get<IAMGroupListResponse>("/v1/iam/groups");
      return data;
    },
    enabled,
    refetchOnWindowFocus: true
  });
}

export function useCreateIamUser() {
  return useMutation({
    mutationFn: async (payload: IAMUserCreateRequest) => {
      const { data } = await apiClient.post<IAMUserSummary>("/v1/iam/users", payload);
      return data;
    }
  });
}

export function useUpdateIamUser() {
  return useMutation({
    mutationFn: async (payload: { userId: string; update: IAMUserUpdateRequest }) => {
      const { data } = await apiClient.patch<IAMUserSummary>(
        `/v1/iam/users/${encodeURIComponent(payload.userId)}`,
        payload.update
      );
      return data;
    }
  });
}

export function useDeleteIamUser() {
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await apiClient.delete(`/v1/iam/users/${encodeURIComponent(userId)}`);
      return data;
    }
  });
}

export function useResetIamUserPassword() {
  return useMutation({
    mutationFn: async (payload: { userId: string; password: string; temporary?: boolean }) => {
      const { data } = await apiClient.post(
        `/v1/iam/users/${encodeURIComponent(payload.userId)}/reset-password`,
        {
          password: payload.password,
          temporary: Boolean(payload.temporary)
        }
      );
      return data;
    }
  });
}

export function useAddUserToGroup() {
  return useMutation({
    mutationFn: async (payload: { userId: string; groupId: string }) => {
      const { data } = await apiClient.post(
        `/v1/iam/users/${encodeURIComponent(payload.userId)}/groups/${encodeURIComponent(payload.groupId)}`
      );
      return data;
    }
  });
}

export function useRemoveUserFromGroup() {
  return useMutation({
    mutationFn: async (payload: { userId: string; groupId: string }) => {
      const { data } = await apiClient.delete(
        `/v1/iam/users/${encodeURIComponent(payload.userId)}/groups/${encodeURIComponent(payload.groupId)}`
      );
      return data;
    }
  });
}

export function useCreateIamGroup() {
  return useMutation({
    mutationFn: async (payload: { name: string }) => {
      const { data } = await apiClient.post<IAMGroupSummary>("/v1/iam/groups", payload);
      return data;
    }
  });
}

export function useUpdateIamGroup() {
  return useMutation({
    mutationFn: async (payload: { groupId: string; name: string }) => {
      const { data } = await apiClient.patch<IAMGroupSummary>(
        `/v1/iam/groups/${encodeURIComponent(payload.groupId)}`,
        { name: payload.name }
      );
      return data;
    }
  });
}

export function useDeleteIamGroup() {
  return useMutation({
    mutationFn: async (groupId: string) => {
      const { data } = await apiClient.delete(`/v1/iam/groups/${encodeURIComponent(groupId)}`);
      return data;
    }
  });
}

export function useEmailPreview(
  account: string | undefined,
  date: string | undefined,
  folder: string | undefined,
  filename: string | undefined,
  enabled = false
) {
  return useQuery({
    queryKey: ["email-preview", account, date, folder, filename],
    queryFn: async () => {
      const { data } = await apiClient.get<EmailPreview>(
        `/v1/accounts/${encodeURIComponent(account ?? "")}/emails/${date}/${encodeURIComponent(folder ?? "")}/${encodeURIComponent(filename ?? "")}/preview`
      );
      return data;
    },
    enabled: Boolean(enabled && account && date && folder && filename)
  });
}

export function buildAttachmentDownloadUrl(
  account: string,
  date: string,
  folder: string,
  filename: string,
  attachmentId: number
) {
  return `${API_BASE_URL}/v1/accounts/${encodeURIComponent(account)}/emails/${date}/${encodeURIComponent(folder)}/${encodeURIComponent(filename)}/attachments/${attachmentId}`;
}

export function useSchedulerToggle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (running: boolean) => {
      const endpoint = running ? "/v1/system/scheduler/stop" : "/v1/system/scheduler/start";
      const { data } = await apiClient.post(endpoint);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
    }
  });
}

export interface RunRangeRequest {
  start_date?: string;
  end_date?: string;
  full_history?: boolean;
}

export interface RunRangeResponse {
  accepted: boolean;
  start_date: string;
  end_date: string;
}

export interface CatchUpResponse {
  accepted: boolean;
  start_date?: string | null;
  end_date?: string | null;
  missing_days: number;
  message: string;
}

export interface RunAccountRequest {
  account: string;
  date?: string;
  full_history?: boolean;
  start_date?: string;
  end_date?: string;
}

export interface RunAccountResponse {
  accepted: boolean;
  account: string;
  mode: string;
}

export interface MetadataRebuildRequest {
  account?: string;
  start_date?: string;
  end_date?: string;
}

export interface MetadataRebuildResponse {
  accepted: boolean;
  account?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface MetadataRebuildStatusResponse {
  running: boolean;
  last_started_iso?: string | null;
  last_completed_iso?: string | null;
  last_error?: string | null;
  last_summary?: Record<string, unknown> | null;
  account?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface DatabaseBackupExportResponse {
  accepted: boolean;
  backend: string;
}

export interface DatabaseBackupStatusResponse {
  running: boolean;
  last_started_iso?: string | null;
  last_completed_iso?: string | null;
  last_error?: string | null;
  last_result?: Record<string, unknown> | null;
}

export interface DatabaseBackupItem {
  filename: string;
  checksum_filename: string;
  manifest_filename: string;
  backend: string;
  row_count: number;
  size_bytes: number;
  checksum_sha256: string;
  created_at_iso: string;
}

export interface DatabaseBackupListResponse {
  total: number;
  backups: DatabaseBackupItem[];
}

export interface DatabaseBackupImportValidateResponse {
  valid: boolean;
  filename: string;
  backend: string;
  row_count: number;
  checksum_sha256: string;
  size_bytes: number;
  created_at_iso: string;
}

export interface DatabaseBackupImportRequest {
  filename: string;
  mode: "replace" | "merge";
  dry_run?: boolean;
}

export interface DatabaseBackupImportResponse {
  accepted: boolean;
  filename: string;
  mode: "replace" | "merge";
  dry_run: boolean;
}

export interface DatabaseBackupImportStatusResponse {
  running: boolean;
  last_started_iso?: string | null;
  last_completed_iso?: string | null;
  last_error?: string | null;
  last_result?: Record<string, unknown> | null;
}

export function useRunRange() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RunRangeRequest) => {
      const { data } = await apiClient.post<RunRangeResponse>("/v1/system/run-range", payload);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
    }
  });
}

export function useRunCatchUp() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<CatchUpResponse>("/v1/system/catch-up");
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
    }
  });
}

export function useRunAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RunAccountRequest) => {
      const { data } = await apiClient.post<RunAccountResponse>("/v1/system/run-account", payload);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
    }
  });
}

export function useRebuildMetadataIndex() {
  return useMutation({
    mutationFn: async (payload: MetadataRebuildRequest) => {
      const { data } = await apiClient.post<MetadataRebuildResponse>(
        "/v1/system/metadata-index/rebuild",
        payload
      );
      return data;
    }
  });
}

export function useMetadataRebuildStatus(pollMs = 10_000) {
  return useQuery({
    queryKey: ["metadata-rebuild-status"],
    queryFn: async () => {
      const { data } = await apiClient.get<MetadataRebuildStatusResponse>(
        "/v1/system/metadata-index/status"
      );
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export function useExportDatabaseBackup() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<DatabaseBackupExportResponse>(
        "/v1/system/db-backup/export"
      );
      return data;
    }
  });
}

export function useDatabaseBackupStatus(pollMs = 10_000) {
  return useQuery({
    queryKey: ["db-backup-status"],
    queryFn: async () => {
      const { data } = await apiClient.get<DatabaseBackupStatusResponse>(
        "/v1/system/db-backup/status"
      );
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export function useDatabaseBackups(pollMs = 15_000) {
  return useQuery({
    queryKey: ["db-backup-list"],
    queryFn: async () => {
      const { data } = await apiClient.get<DatabaseBackupListResponse>(
        "/v1/system/db-backup/list"
      );
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export function buildDatabaseBackupDownloadUrl(filename: string) {
  return `${API_BASE_URL}/v1/system/db-backup/download/${encodeURIComponent(filename)}`;
}

export function useValidateDatabaseBackupImport() {
  return useMutation({
    mutationFn: async (payload: { filename: string }) => {
      const { data } = await apiClient.post<DatabaseBackupImportValidateResponse>(
        "/v1/system/db-backup/import/validate",
        payload
      );
      return data;
    }
  });
}

export function useImportDatabaseBackup() {
  return useMutation({
    mutationFn: async (payload: DatabaseBackupImportRequest) => {
      const { data } = await apiClient.post<DatabaseBackupImportResponse>(
        "/v1/system/db-backup/import",
        payload
      );
      return data;
    }
  });
}

export function useDatabaseBackupImportStatus(pollMs = 10_000) {
  return useQuery({
    queryKey: ["db-backup-import-status"],
    queryFn: async () => {
      const { data } = await apiClient.get<DatabaseBackupImportStatusResponse>(
        "/v1/system/db-backup/import/status"
      );
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export function useDeleteAccount() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { account: string }) => {
      const { data } = await apiClient.delete(`/v1/accounts/${encodeURIComponent(payload.account)}`);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: systemStatusKey });
      client.invalidateQueries({ queryKey: configKey });
    }
  });
}

export const configKey = ["config"] as const;

export function useSystemConfig() {
  return useQuery({
    queryKey: configKey,
    queryFn: async () => {
      const { data } = await apiClient.get<ConfigDocument>("/v1/config");
      return data;
    },
    staleTime: 30_000
  });
}

export function useUpdateConfig() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ConfigDocument) => {
      const { data } = await apiClient.put<ConfigDocument>("/v1/config", payload);
      return data;
    },
    onSuccess: (data) => {
      client.setQueryData(configKey, data);
    }
  });
}

export function useValidateConfig() {
  return useMutation({
    mutationFn: async (payload: ConfigDocument) => {
      const { data } = await apiClient.post<ConfigDocument>("/v1/config/validate", payload);
      return data;
    }
  });
}

export interface ImapFoldersRequest {
  host: string;
  port?: number;
  username: string;
  password: string;
  timeout?: number;
}

export interface ImapFoldersResponse {
  folders: string[];
}

export function useDiscoverImapFolders() {
  return useMutation({
    mutationFn: async (payload: ImapFoldersRequest) => {
      const { data } = await apiClient.post<ImapFoldersResponse>("/v1/imap/folders", payload);
      return data.folders;
    }
  });
}

export const accountsKey = ["accounts"] as const;

export function useAccounts() {
  return useQuery({
    queryKey: accountsKey,
    queryFn: async () => {
      const { data } = await apiClient.get<AccountListResponse>("/v1/accounts");
      return data;
    },
    staleTime: 5 * 60 * 1000
  });
}

export function useAccountDates(account?: string, year?: string, limit = 25, offset = 0) {
  return useQuery({
    queryKey: ["accounts", account, "years", year, "dates", limit, offset],
    queryFn: async () => {
      if (!account || !year) {
        throw new Error("Account and year required");
      }
      const { data } = await apiClient.get<DateListResponse>(
        `/v1/accounts/${encodeURIComponent(account)}/dates`,
        { params: { year, limit, offset } }
      );
      return data;
    },
    enabled: Boolean(account && year),
    placeholderData: keepPreviousData
  });
}

export function useEmails(account?: string, date?: string, folder?: string, limit = 200, offset = 0) {
  return useQuery({
    queryKey: ["accounts", account, "dates", date, "emails", folder ?? "all", limit, offset],
    queryFn: async () => {
      if (!account || !date) {
        throw new Error("Account and date required");
      }
      const { data } = await apiClient.get<EmailListResponse>(
        `/v1/accounts/${encodeURIComponent(account)}/emails/${date}`,
        {
          params: {
            ...(folder ? { folder } : {}),
            limit,
            offset
          }
        }
      );
      return normalizeEmailListResponse(data);
    },
    enabled: Boolean(account && date),
    placeholderData: keepPreviousData
  });
}

export interface SearchResult {
  account: string;
  date: string;
  email: EmailInfo;
}

export interface SearchResponse {
  total: number;
  results: SearchResult[];
}

export interface SearchQueryParams {
  subject?: string;
  sender?: string;
  recipient?: string;
  account?: string;
  folder?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export function useSearchEmails(params: SearchQueryParams, enabled: boolean) {
  return useQuery({
    queryKey: ["search", params],
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {};
      if (params.subject) queryParams.subject = params.subject;
      if (params.sender) queryParams.from = params.sender;
      if (params.recipient) queryParams.to = params.recipient;
      if (params.account) queryParams.account = params.account;
      if (params.folder) queryParams.folder = params.folder;
      if (params.date_from) queryParams.date_from = params.date_from;
      if (params.date_to) queryParams.date_to = params.date_to;
      if (typeof params.limit === "number") queryParams.limit = params.limit;
      if (typeof params.offset === "number") queryParams.offset = params.offset;

      const { data } = await apiClient.get<SearchResponse>("/v1/search", {
        params: queryParams
      });
      return normalizeSearchResponse(data);
    },
    enabled,
    placeholderData: keepPreviousData
  });
}

export interface ActivityEvent {
  type: string;
  timestamp: string;
  target_date?: string | null;
  report?: Record<string, unknown> | null;
  error?: string | null;
  account?: string | null;
  status?: string | null;
  stage?: string | null;
  progress?: number | null;
  total_messages?: number | null;
}

export interface ActivityListResponse {
  total: number;
  events: ActivityEvent[];
}

export interface AuditEvent {
  timestamp: string;
  ip: string;
  user: string;
  method: string;
  path: string;
  status: number;
  event?: string | null;
  request_id?: string | null;
  session_id?: string | null;
  user_agent?: string | null;
  error?: string | null;
}

export interface AuditListResponse {
  total: number;
  events: AuditEvent[];
}

export interface S3TieringLockInfo {
  present: boolean;
  path: string;
  pid?: number | null;
  pid_active?: boolean | null;
  age_seconds?: number | null;
  stale: boolean;
}

export interface S3TieringLastRun {
  started_at?: string | null;
  ended_at?: string | null;
  duration_seconds?: number | null;
  status?: string | null;
  phase?: string | null;
  message?: string | null;
  offloaded_accounts: number;
  pruned_files: number;
  skipped_files: number;
}

export interface S3TieringStatusResponse {
  backup_mode: string;
  enabled: boolean;
  bucket?: string | null;
  region?: string | null;
  prefix?: string | null;
  endpoint_url?: string | null;
  local_hot_retention_days: number;
  min_free_space_gb: number;
  allow_local_prune: boolean;
  aws_credentials_present: boolean;
  local_data_path: string;
  free_space_gb?: number | null;
  lock: S3TieringLockInfo;
  last_run?: S3TieringLastRun | null;
}

export interface S3TieringPrunePrecheckResponse {
  allowed: boolean;
  reasons: string[];
  candidate_files: number;
  candidate_size_bytes: number;
  impacted_accounts: string[];
  free_space_gb?: number | null;
  min_free_space_gb: number;
  last_offload_status?: string | null;
  last_offload_ended_at?: string | null;
}

export interface S3TieringOffloadVerification {
  enabled: boolean;
  sampled_files: number;
  verified_files: number;
  failed_files: number;
  size_mismatches: number;
  warnings: string[];
}

export type S3TieringAction = "validate" | "dry-run" | "offload" | "prune" | "cycle" | "unlock-stale";

export interface S3TieringActionResponse {
  accepted: boolean;
  action: S3TieringAction;
  exit_code: number;
  output_tail: string[];
  status?: S3TieringStatusResponse | null;
  prune_precheck?: S3TieringPrunePrecheckResponse | null;
  offload_verification?: S3TieringOffloadVerification | null;
}

export interface S3TieringConfigureRequest {
  backup_mode: "local_tiered_s3" | "local_mirror_s3";
  bucket: string;
  region: string;
  prefix: string;
  endpoint_url?: string;
  local_hot_retention_days: number;
  min_free_space_gb: number;
  allow_local_prune: boolean;
  aws_access_key_id?: string;
  aws_secret_access_key?: string;
}

export function useActivityEvents(limit = 100, pollMs = 30_000, enabled = true, offset = 0, eventType?: string, account?: string) {
  return useQuery({
    queryKey: ["activity", limit, offset, eventType, account],
    queryFn: async () => {
      const { data } = await apiClient.get<ActivityListResponse>("/v1/system/activity", {
        params: { limit, offset, type: eventType, account }
      });
      return data;
    },
    refetchInterval: enabled ? pollMs : false,
    refetchOnWindowFocus: true,
    enabled
  });
}

export function useAuditEvents(
  limit = 50,
  offset = 0,
  pollMs = 30_000,
  method?: string,
  status?: number,
  user?: string,
  event?: string,
  q?: string
) {
  return useQuery({
    queryKey: ["audit", limit, offset, method, status, user, event, q],
    queryFn: async () => {
      const { data } = await apiClient.get<AuditListResponse>("/v1/system/audit", {
        params: { limit, offset, method, status, user, event, q }
      });
      return data;
    },
    refetchInterval: pollMs,
    refetchOnWindowFocus: true
  });
}

export const s3TieringStatusKey = ["system", "s3-tiering", "status"] as const;

export function useS3TieringStatus(pollMs = 30_000, enabled = true) {
  return useQuery({
    queryKey: s3TieringStatusKey,
    queryFn: async () => {
      const { data } = await apiClient.get<S3TieringStatusResponse>("/v1/system/s3-tiering/status");
      return data;
    },
    enabled,
    refetchInterval: enabled ? pollMs : false,
    refetchOnWindowFocus: true,
  });
}

export function useRunS3TieringAction() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (action: S3TieringAction) => {
      const { data } = await apiClient.post<S3TieringActionResponse>(`/v1/system/s3-tiering/actions/${action}`);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: s3TieringStatusKey });
    },
  });
}

export function useS3TieringPrunePrecheck() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.get<S3TieringPrunePrecheckResponse>("/v1/system/s3-tiering/precheck/prune");
      return data;
    },
  });
}

export function useConfigureS3Tiering() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (payload: S3TieringConfigureRequest) => {
      const { data } = await apiClient.post<S3TieringStatusResponse>("/v1/system/s3-tiering/configure", payload);
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: s3TieringStatusKey });
      client.invalidateQueries({ queryKey: configKey });
    },
  });
}
